import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getLocationSuggestions, formatLocation } from "../api";
import { updateSearchURL } from "../utils/urlParams";
import { useNavigate } from "react-router-dom";
import { detectUserCountry } from "../api/services/locationService";

const FormContext = createContext();

export const useFormContext = () => {
  const context = useContext(FormContext); // It must look for the instance above
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

const getStorageItem = (key, initialValue) => {
  if (typeof window === "undefined") return initialValue;

  try {
    const item = window.localStorage.getItem(key);
    return item ? JSON.parse(item) : initialValue;
  } catch {
    return initialValue;
  }
};

const setStorageItem = (key, value) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {}
};

// --- Search session (frontend guard) ---
const SEARCH_SESSION_MS = 20 * 60 * 1000; // 20 minutes

const now = () => Date.now();

const getSessionMeta = () => getStorageItem("search_session_meta", null);

const setSessionMeta = (meta) => setStorageItem("search_session_meta", meta);

const clearSessionMeta = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem("search_session_meta");
  window.localStorage.removeItem("active_search_id");
  // keep originalSearchURL if you want; or clear it too
};

export const FormProvider = ({ children, canUseTestMode = false }) => {
  const [isTestMode, setIsTestMode] = useState(() =>
    getStorageItem("form_isTestMode", false),
  );

  // --- Form state ---
  const [sameLocation, setSameLocation] = useState(() =>
    getStorageItem("form_sameLocation", true),
  );
  const [pickup, setPickup] = useState(() =>
    getStorageItem("form_pickup", ""),
  );
  const [pickupLocationObj, setPickupLocationObj] = useState(() =>
    getStorageItem("form_pickupLocationObj", null),
  );
  const [pickupGDSCode, setPickupGDSCode] = useState(() =>
    getStorageItem("form_pickupGDSCode", ""),
  );
  const [dropoff, setDropoff] = useState(() =>
    getStorageItem("form_dropoff", ""),
  );
  const [dropoffLocationObj, setDropoffLocationObj] = useState(() =>
    getStorageItem("form_dropoffLocationObj", null),
  );
  const [dropOffGDSCode, setDropOffGDSCode] = useState(() =>
    getStorageItem("form_dropOffGDSCode", ""),
  );

  // Date and time
  const [pickupDate, setPickupDate] = useState(() =>
    getStorageItem("form_pickupDate", ""),
  );
  const [pickupTime, setPickupTime] = useState(() =>
    getStorageItem("form_pickupTime", "10:00"),
  );
  const [dropoffDate, setDropoffDate] = useState(() =>
    getStorageItem("form_dropoffDate", ""),
  );
  const [dropoffTime, setDropoffTime] = useState(() =>
    getStorageItem("form_dropoffTime", "10:00"),
  );

  // Driver details
  const [driverCountry, setDriverCountry] = useState(() =>
    getStorageItem("form_driverCountry", ""),
  );
  const [driverAge, setDriverAge] = useState(() =>
    getStorageItem("form_driverAge", "35"),
  );

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [userManuallySelectedCountry, setUserManuallySelectedCountry] =
    useState(() => getStorageItem("form_manualCountry", false));

  const [localPickupTime, setLocalPickupTime] = useState(() =>
    getStorageItem("form_pickupTime", "10:00"),
  );
  const [localDropoffTime, setLocalDropoffTime] = useState(() =>
    getStorageItem("form_dropoffTime", "10:00"),
  );

  const navigate = useNavigate();

  const [activeSearchId, setActiveSearchId] = useState(() =>
    getStorageItem("active_search_id", ""),
  );
  const [searchSessionMeta, setSearchSessionMetaState] = useState(() =>
    getSessionMeta(),
  );

  // --- Auto-detect country on first visit ---
  useEffect(() => {
    if (userManuallySelectedCountry) return;
    if (driverCountry) return;

    const detect = async () => {
      const countryCode = await detectUserCountry();
      if (countryCode) setDriverCountry(countryCode);
    };
    detect();
  }, []);

  // --- Sync local time buffers ---
  useEffect(() => {
    setLocalPickupTime(pickupTime);
    setLocalDropoffTime(dropoffTime);
  }, [pickupTime, dropoffTime]);

  // --- Persistence ---
  useEffect(() => {
    setStorageItem("form_sameLocation", sameLocation);
    setStorageItem("form_pickup", pickup);
    setStorageItem("form_pickupLocationObj", pickupLocationObj);
    setStorageItem("form_dropoff", dropoff);
    setStorageItem("form_dropoffLocationObj", dropoffLocationObj);
    setStorageItem("form_pickupGDSCode", pickupGDSCode);
    setStorageItem("form_dropOffGDSCode", dropOffGDSCode);
    setStorageItem("form_pickupDate", pickupDate);
    setStorageItem("form_pickupTime", pickupTime);
    setStorageItem("form_dropoffDate", dropoffDate);
    setStorageItem("form_dropoffTime", dropoffTime);
    setStorageItem("form_driverCountry", driverCountry);
    setStorageItem("form_driverAge", driverAge);
    setStorageItem("form_isTestMode", isTestMode);
  }, [
    sameLocation,
    pickup,
    pickupLocationObj,
    dropoff,
    dropoffLocationObj,
    pickupGDSCode,
    dropOffGDSCode,
    pickupDate,
    pickupTime,
    dropoffDate,
    dropoffTime,
    driverCountry,
    driverAge,
    isTestMode,
  ]);

  useEffect(() => {
    setStorageItem("active_search_id", activeSearchId);
  }, [activeSearchId]);

  useEffect(() => {
    setStorageItem("form_manualCountry", userManuallySelectedCountry);
  }, [userManuallySelectedCountry]);

  // --- Helpers ---
  const resetForm = () => {
    setSameLocation(true);
    setPickup("");
    setPickupLocationObj(null);
    setPickupGDSCode("");
    setDropOffGDSCode("");
    setDropoff("");
    setDropoffLocationObj(null);
    setDriverCountry("US");
    setDriverAge("35");
  };

  const startSearchSession = useCallback(({ searchId, originalUrl }) => {
    const startedAt = now();
    const expiresAt = startedAt + SEARCH_SESSION_MS;
    if (searchId) {
      setActiveSearchId(searchId);
      setStorageItem("active_search_id", searchId);
    }
    if (originalUrl) setStorageItem("originalSearchURL", originalUrl);
    const meta = { startedAt, expiresAt };
    setSessionMeta(meta);
    setSearchSessionMetaState(meta);
    return meta;
  }, []);

  const isSearchSessionExpired = useCallback(() => {
    const meta = getSessionMeta();
    if (!meta?.expiresAt) return false;
    return now() >= meta.expiresAt;
  }, []);

  const getSearchSecondsLeft = useCallback(() => {
    const meta = getSessionMeta();
    if (!meta?.expiresAt) return null;
    return Math.max(0, Math.floor((meta.expiresAt - now()) / 1000));
  }, []);

  const clearSearchSession = useCallback(() => {
    setActiveSearchId("");
    setSearchSessionMetaState(null);
    clearSessionMeta();
  }, []);

  const fetchLocationSuggestions = useCallback(async (query) => {
    if (!query || query.trim().length < 1) return [];
    try {
      return await getLocationSuggestions(query);
    } catch (error) {
      console.error("Error fetching locations:", error);
      return [];
    }
  }, []);

  const formatLocationHelper = useCallback((loc) => formatLocation(loc), []);

  const handleMobileSubmit = () => {
    const effectivePickupCode =
      pickupLocationObj?.location_id || pickupGDSCode || "";
    const effectiveDropoffCode = sameLocation
      ? effectivePickupCode
      : dropoffLocationObj?.location_id || dropOffGDSCode || "";

    if (!driverAge || isNaN(parseInt(driverAge))) {
      alert("Please enter the driver's age.");
      return false;
    }
    if (parseInt(driverAge) < 21) {
      alert("Driver's age must be at least 21 years");
      return false;
    }
    if (!effectivePickupCode) {
      alert("Please select a pickup location");
      return false;
    }
    if (!pickup || !effectivePickupCode) {
      alert("Please select a pickup location");
      return false;
    }
    if (!sameLocation && !dropoff) {
      alert("Please select drop-off location");
      return false;
    }
    if (!sameLocation && !dropoffLocationObj) {
      alert("Please select a valid drop-off location");
      return false;
    }
    if (!pickupDate) {
      alert("Pick-up date is required");
      return false;
    }
    if (!dropoffDate) {
      alert("Drop-off date is required");
      return false;
    }
    if (new Date(dropoffDate) < new Date(pickupDate)) {
      alert("Please make sure the drop-off date is later than the pick-up date");
      return false;
    }
    if (!pickupTime) {
      alert("Pick-up time is required");
      return false;
    }
    if (!dropoffTime) {
      alert("Drop-off time is required");
      return false;
    }

    const searchData = {
      sameLocation,
      pickupLocation: pickup,
      dropoffLocation: sameLocation ? pickup : dropoff,
      pickupCode: effectivePickupCode,
      dropoffCode: effectiveDropoffCode,
      pickupDate,
      pickupTime,
      dropoffDate,
      dropoffTime,
      driverCountry,
      driverAge,
      is_test: isTestMode,
    };
    sessionStorage.setItem("returningFromSearch", "true");
    sessionStorage.removeItem("pageWasRefreshed");
    updateSearchURL(navigate, searchData);
    return true;
  };

  const value = {
    sameLocation,
    setSameLocation,
    pickup,
    setPickup,
    pickupLocationObj,
    setPickupLocationObj,
    pickupGDSCode,
    setPickupGDSCode,
    dropOffGDSCode,
    setDropOffGDSCode,
    dropoff,
    setDropoff,
    dropoffLocationObj,
    setDropoffLocationObj,
    pickupSuggestions,
    setPickupSuggestions,
    dropoffSuggestions,
    setDropoffSuggestions,
    pickupDate,
    setPickupDate,
    pickupTime,
    setPickupTime,
    dropoffDate,
    setDropoffDate,
    dropoffTime,
    setDropoffTime,
    driverCountry,
    setDriverCountry,
    driverAge,
    setDriverAge,
    fetchLocationSuggestions,
    formatLocation: formatLocationHelper,
    resetForm,
    activeSearchId,
    setActiveSearchId,
    startSearchSession,
    isSearchSessionExpired,
    getSearchSecondsLeft,
    clearSearchSession,
    userManuallySelectedCountry,
    setUserManuallySelectedCountry,
    handleMobileSubmit,
    localPickupTime,
    setLocalPickupTime,
    localDropoffTime,
    setLocalDropoffTime,
    canUseTestMode,
    isTestMode,
    setIsTestMode,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};

export default FormProvider;